import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import s from './PdfLink.module.css';

/**
 * How long a printed PDF's object URL stays alive. Revoking it the moment the tab opens would leave
 * that tab pointing at nothing — but an office printing all morning would otherwise hold every blob
 * it ever fetched. A minute is far longer than a print dialog takes and bounds the leak at one PDF.
 */
export const PDF_URL_TTL_MS = 60_000;

/** A PDF handed to a new tab: where it came from, its object URL, and when that URL dies. */
export interface OpenedPdf {
  path: string;
  url: string;
  opened: boolean;
  expiresAt: number;
}

/** Fetches the PDF at `path` and hands it to a new tab. `opened` is false when the browser blocked the tab. */
export async function openPdf(path: string): Promise<OpenedPdf> {
  const blob = await api.pdf(path);
  const url = URL.createObjectURL(blob);
  const win = window.open(url, '_blank');
  setTimeout(() => URL.revokeObjectURL(url), PDF_URL_TTL_MS);
  return { path, url, opened: Boolean(win), expiresAt: Date.now() + PDF_URL_TTL_MS };
}

/**
 * The way to a PDF whose new tab the browser blocked. "Open PDF" is a plain link to the object URL,
 * which a click always opens — while the URL lives. After a minute the URL is revoked, and a link to
 * it would do nothing at all when clicked; so it becomes "Open again", which fetches the PDF afresh.
 *
 * Give it a `key` of the PDF's URL, so a new print starts it over.
 */
export function BlockedPdfLink({ pdf }: { pdf: OpenedPdf }) {
  const [current, setCurrent] = useState(pdf);
  const [expired, setExpired] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    setExpired(false);
    // A second early, so the link is never clicked in the instant after its URL is gone.
    const t = setTimeout(() => setExpired(true), Math.max(0, current.expiresAt - Date.now() - 1000));
    return () => clearTimeout(t);
  }, [current]);

  async function again() {
    setBusy(true);
    setError(null);
    try {
      setCurrent(await openPdf(current.path));
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      {expired ? (
        <button type="button" className={`link ${s.plAgain}`} disabled={busy} onClick={() => void again()}>
          Open again
        </button>
      ) : (
        <a className="link" href={current.url} target="_blank" rel="noreferrer">
          Open PDF
        </a>
      )}
      {error ? (
        <span role="alert" className={s.plError}>
          {' '}
          {error instanceof Error ? error.message : 'Something went wrong'}
        </span>
      ) : null}
    </>
  );
}
